import Layout from '../components/Layout/Layout';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';

import TaxCalculatorHero from '../components/FeatureBlocks/TaxCalculatorHero/TaxCalculatorHero';
import PromoBlockLeft from '../components/FeatureBlocks/PromoBlockLeft/PromoBlockLeft';
import { usePromoCode } from '../hooks/usePromoCode';

import '../styles/tax-calculator.scss';
import DiscoverLinkBlock from '../components/FeatureBlocks/DiscoverLinkBlock/DiscoverLinkBlock';
import TaxFilingBlock from '../components/FeatureBlocks/TaxFilingBlock/TaxFilingBlock';
import TaxFAQBlock from '../components/FeatureBlocks/TaxFAQBlock/TaxFAQBlock';
import StarReviews from '../components/StarReviews/StarReviews';

const blogLinkData = [
    {
        id: 'diy',
        url: '/blog/the-diy-tax-filing-checklist-that-every-small-business-owner-needs'
    },
    {
        id: 'deductions',
        url: '//help.honeybook.com/en/articles/10276799-what-are-tax-deductions-and-how-do-i-maximize-them-in-the-usa'
    },
    {
        id: 'expenses',
        url: '/blog/how-to-track-expenses-for-small-business-owners'
    },
    {
        id: 'creative',
        url: '/blog/creative-business-tax-deductions'
    }
];

const stateDescriptions: Record<string, string> = {
    CA:
        "Free California self-employment tax calculator. Estimate what you owe with state income tax from 1% to 13.3% in mind. Try HoneyBook's free calculator.",
    TX:
        "Free Texas self-employment tax calculator. Texas has no individual income tax, so estimate what you owe in minutes with HoneyBook's free tax calculator.",
    FL:
        "Free Florida self-employment tax calculator. Florida has no state individual income tax, so estimate what you owe in minutes with HoneyBook's calculator.",
    NY:
        "Free New York self-employment tax calculator. Estimate what you owe with NY's 4% to 10.9% graduated income tax in mind, using HoneyBook's free calculator.",
    PA:
        "Free Pennsylvania self-employment tax calculator. Estimate what you owe with PA's flat 3.07% state income tax in mind, using HoneyBook's free calculator.",
    IL:
        "Free Illinois self-employment tax calculator. Estimate what you owe with IL's flat 4.95% individual income tax rate in mind, using HoneyBook's calculator.",
    OH:
        "Free Ohio self-employment tax calculator. Estimate what you owe with Ohio's 2.765% to 3.99% income tax rates in mind, using HoneyBook's free calculator.",
    GA:
        "Free Georgia self-employment tax calculator. Estimate what you owe with GA's graduated 1% to 5.75% income tax in mind, using HoneyBook's free calculator.",
    NC:
        "Free North Carolina self-employment tax calculator. Estimate what you owe with NC's flat 4.75% income tax rate in mind, using HoneyBook's free calculator.",
    MI:
        "Free Michigan self-employment tax calculator. Estimate what you owe with Michigan's flat 4.25% individual income tax in mind, using HoneyBook's calculator."
};

interface Props {
    pageContext: {
        USState: string;
        isCtaTest?: boolean;
    };
}

function TaxCalculatorPage({
    pageContext: { USState, isCtaTest = false }
}: Props): JSX.Element {
    const { t } = useTranslation();
    const { promoCode } = usePromoCode();

    const pageTitle = USState
        ? `Free Self-Employment Tax Calculator for ${t(`states.${USState}`)}`
        : 'Free Self-Employment Tax Calculator';

    const pageUrl = USState
        ? `https://www.honeybook.com/tools/self-employment-tax-calculator-${t(
              `states.${USState}`
          )
              .toLowerCase()
              .replace(/ /g, '-')}`
        : 'https://www.honeybook.com/tools/self-employment-tax-calculator';

    const ogTags = {
        url: pageUrl,
        title: pageTitle,
        description:
            stateDescriptions[USState] ??
            "Calculate the total income tax estimation for your independent business with HoneyBook's free tax calculator. Start your 30-day free trial with HoneyBook."
    };

    return (
        <Layout
            customClass="tax-calculator-page"
            ogTags={ogTags}
            isCtaTest={isCtaTest}
        >
            <TaxCalculatorHero USState={USState} />
            <TaxFAQBlock USState={USState} />
            <TaxFilingBlock />
            <DiscoverLinkBlock
                customTitle={t(`taxCalculator.discoverLinkBlock.title`)}
                customSubtitle={t(`taxCalculator.discoverLinkBlock.subtitle`)}
                linkData={blogLinkData}
            />
            <StarReviews />
            <PromoBlockLeft
                customClass="tax-calculator-page--new__promo-block-left promo-block-left--yellow"
                title={t(`${promoCode}.promoBlockLeft.title`)}
                subtitle={t(`${promoCode}.promoBlockLeft.subtitle`, {
                    defaultValue: null
                })}
                source="promo block left - yellow"
                promoCode={promoCode}
            />
        </Layout>
    );
}

export const query = graphql`
    query ($language: String!) {
        locales: allLocale(
            filter: {
                ns: { in: ["taxCalculator", "common", "faqs"] }
                language: { eq: $language }
            }
        ) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
    }
`;

export default TaxCalculatorPage;
